import React from "react";

/**
 * Barre de progression stylée Travel.
 *
 * - Gradient primary → secondary (CSS vars du thème Travel)
 * - Animation : fill au mount (transition width), shimmer subtil, glow pulsant
 * - Pourcentage affiché à droite
 *
 * Props :
 *   percent : 0-100 (number)
 *   showLabel : boolean (default true)
 *   size : "sm" | "md" (default "md")
 */
const styles = `
.tr-progress {
  display: flex; align-items: center; gap: 12px;
  width: 100%;
}
.tr-progress__track {
  flex: 1;
  position: relative;
  height: 10px;
  background: color-mix(in oklab, var(--primary, #1b579e) 10%, #f3f0e8);
  border-radius: 999px;
  overflow: hidden;
  isolation: isolate;
}
.tr-progress__track--sm { height: 6px; }

.tr-progress__fill {
  position: absolute;
  inset: 0 auto 0 0;
  height: 100%;
  border-radius: 999px;
  background: linear-gradient(90deg,
    var(--primary, #1b579e) 0%,
    color-mix(in oklab, var(--primary, #1b579e) 60%, var(--secondary, #e86a4e)) 50%,
    var(--secondary, #e86a4e) 100%);
  transition: width 1.1s cubic-bezier(.16, 1, .3, 1);
  box-shadow: 0 0 0 1px color-mix(in oklab, var(--primary, #1b579e) 30%, transparent) inset;
}
[dir="rtl"] .tr-progress__fill {
  inset: 0 0 0 auto;
}

/* Shimmer animé */
.tr-progress__fill::before {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg,
    transparent 0%,
    rgba(255,255,255,.22) 50%,
    transparent 100%);
  background-size: 200% 100%;
  animation: tr-progress-shimmer 2.4s linear infinite;
  border-radius: inherit;
}
@keyframes tr-progress-shimmer {
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* Glow pulsant au bord droit */
.tr-progress__fill::after {
  content: "";
  position: absolute;
  top: -3px; bottom: -3px;
  right: -3px;
  width: 8px;
  border-radius: 50%;
  background: var(--secondary, #e86a4e);
  box-shadow: 0 0 12px 2px color-mix(in oklab, var(--secondary, #e86a4e) 60%, transparent);
  animation: tr-progress-pulse 1.8s ease-in-out infinite;
  opacity: .9;
}
[dir="rtl"] .tr-progress__fill::after {
  right: auto;
  left: -3px;
}
@keyframes tr-progress-pulse {
  0%, 100% { transform: scale(1); opacity: .9; }
  50% { transform: scale(1.25); opacity: 1; }
}

.tr-progress__fill[data-complete="true"]::after {
  display: none;
}

.tr-progress__label {
  font-size: 14px;
  font-weight: 700;
  color: var(--ink, #121417);
  font-variant-numeric: tabular-nums;
  min-width: 3em;
  text-align: end;
}
`;

export default function Progress({ percent = 0, showLabel = true, size = "md" }) {
    const safePercent = Math.max(0, Math.min(100, Number(percent) || 0));
    const complete = safePercent >= 98;
    const sizeClass = size === "sm" ? "tr-progress__track--sm" : "";

    return (
        <>
            <style dangerouslySetInnerHTML={{ __html: styles }} />
            <div className="tr-progress" role="progressbar" aria-valuenow={Math.round(safePercent)} aria-valuemin={0} aria-valuemax={100}>
                <div className={`tr-progress__track ${sizeClass}`}>
                    <div
                        className="tr-progress__fill"
                        style={{ width: `${safePercent}%` }}
                        data-complete={complete ? "true" : "false"}
                    />
                </div>
                {showLabel && (
                    <span className="tr-progress__label">{Math.round(safePercent)}%</span>
                )}
            </div>
        </>
    );
}
